import { Camera } from 'lucide-react'
import { motion } from 'motion/react'
import Link from 'next/link'
import { useState } from 'react'
import type { User } from '@/src/services/user'
import { cn } from '@/src/utils/styles'
import { Button } from '../ui/button'
import { Back } from '../ui/icons/back'
import {
  ListRow,
  ListRowButton,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../ui/list-row'
import { type Avatar, AvatarImage, DEFAULT_AVATAR } from './avatar'
import { AvatarDialog } from './avatar-dialog'

export function ManageAccount({
  user,
  avatar,
  onClickBack,
  onAvatarChange,
  onEditName,
  onEditEmail,
  showContainer = true,
  showDeleteAccount = false,
}: {
  user: User
  avatar?: Avatar
  onClickBack?: () => void
  onAvatarChange?: (avatar: Avatar) => void
  onEditName?: () => void
  onEditEmail?: () => void
  showContainer?: boolean
  showDeleteAccount?: boolean
}) {
  const [isAvatarDialogOpen, setIsAvatarDialogOpen] = useState(false)

  const handleAvatarSelect = (avatar: Avatar) => {
    onAvatarChange?.(avatar)
  }

  const content = (
    <>
      {/* Header */}
      {onClickBack && (
        <div className='flex items-center min-h-32 gap-5 w-full p-page-gutter'>
          <Button onClick={onClickBack} type='button' variant='ghost'>
            <Back width={24} height={24} />
            <span className='sr-only'>Back</span>
          </Button>
          <h1 className='flex-1 text-center text-primary text-xl font-semibold'>
            Account
          </h1>
          <div className='min-w-6 min-h-6' />
        </div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={cn('flex flex-col w-full', {
          'p-page-gutter pt-0': showContainer,
        })}
      >
        {/* Avatar */}
        <div className='relative mx-auto mb-8'>
          {user.id === 'guest' ? (
            <div className='w-32 h-32 flex items-center justify-center rounded-full border-2 border-dotted border-tertiary-100 opacity-40'>
              <Camera className='w-16 h-16 text-tertiary-100' />
            </div>
          ) : (
            <button
              onClick={() => setIsAvatarDialogOpen(true)}
              className='rounded-full hover:opacity-80 transition-opacity'
            >
              <AvatarImage
                avatar={avatar || DEFAULT_AVATAR}
                size={128}
                className='w-32 h-32'
              />
            </button>
          )}
          {user.id !== 'guest' && (
            <button
              onClick={() => setIsAvatarDialogOpen(true)}
              className='absolute bottom-0 right-0 w-8 h-8 bg-link rounded-3xl outline outline-2 outline-background inline-flex flex-col justify-center items-center hover:bg-link-hover transition-colors'
            >
              <Camera size={24} className='text-background' />
            </button>
          )}
        </div>

        <div className='flex flex-col pt-4 -mx-4'>
          {onEditName ? (
            <ListRowButton size='md' className='py-3' onClick={onEditName}>
              <ListRowSide className='flex-row items-center gap-4'>
                <ListRowSideName>Display name</ListRowSideName>
              </ListRowSide>
              <ListRowSide showIndicator>
                <span className='text-secondary-100'>{user?.name}</span>
              </ListRowSide>
            </ListRowButton>
          ) : (
            <ListRow size='md' className='py-3'>
              <ListRowSide className='flex-row items-center gap-4'>
                <ListRowSideName>Display name</ListRowSideName>
              </ListRowSide>
              <ListRowSide>
                <span className='text-secondary-100'>{user?.name}</span>
              </ListRowSide>
            </ListRow>
          )}

          {user.email && onEditEmail ? (
            <ListRowButton size='md' className='py-3' onClick={onEditEmail}>
              <ListRowSide className='flex-row items-center gap-4'>
                <ListRowSideName>Email address</ListRowSideName>
              </ListRowSide>
              <ListRowSide showIndicator>
                <span className='text-secondary-100'>{user.email}</span>
              </ListRowSide>
            </ListRowButton>
          ) : user.email ? (
            <ListRow size='md' className='py-3'>
              <ListRowSide className='flex-row items-center gap-4'>
                <ListRowSideName>Email address</ListRowSideName>
              </ListRowSide>
              <ListRowSide>
                <span className='text-secondary-100'>{user.email}</span>
              </ListRowSide>
            </ListRow>
          ) : null}

          {showDeleteAccount && (
            <>
              <hr className='grow border-border1 my-4 mx-4' />
              <ListRowButton size='md' className='py-3' asChild>
                <Link href='/offboarding'>
                  <ListRowSide>
                    <ListRowSideName className='text-danger-text'>
                      Close account
                    </ListRowSideName>
                    <ListRowSideDescription>
                      Permanently delete your account
                    </ListRowSideDescription>
                  </ListRowSide>
                  <ListRowSide showIndicator />
                </Link>
              </ListRowButton>
            </>
          )}
        </div>
      </motion.div>

      {/* Avatar Dialog */}
      {user.id !== 'guest' && (
        <AvatarDialog
          isOpen={isAvatarDialogOpen}
          onOpenChange={setIsAvatarDialogOpen}
          currentAvatar={avatar}
          onAvatarSelect={handleAvatarSelect}
        />
      )}
    </>
  )

  if (showContainer) {
    return (
      <div className='w-full bg-background rounded-[32px] flex flex-col items-center'>
        {content}
      </div>
    )
  }

  return content
}
